import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {delay, map} from 'rxjs/operators';

import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {DtoDetail} from '../../../../core/shared/dto/dto.detail';
import {DtoTemplate} from '../../../../core/shared/dto/dto-template';
import {DtoImportObjectContext} from '../../../../core/shared/dto/dto.import.object.context';
import {DeleteResponseDto} from '../../../../core/shared/dto/delete-response.dto';
import {DeletionType} from '../../../../core/shared/entity/enum/deletion-type.enum';
import {MvsFormDto} from '../../../../core/shared/dto/form/dto/mvs-form.dto';
import {FormActionEnum} from '../../../../core/shared/service/crud/enum/form-action.enum';
import {FormModeEnum} from '../../../../core/shared/dto/object/data/form-mode.enum';
import {MvsObjectActionEnum} from '../../../../core/shared/service/object/mvs-object-action.enum';
import {ObjectChangeInformationActionEnum} from '../../../../core/shared/logic/object-change-information';

/**
 * One stored row of the mock. Deliberately a FLAT bag of primitives (number | string | null) and
 * nothing else — no nested objects, no {@code Date}s, no {@code form}. That is what lets
 * {@link XxAutosaveFocusMockService.cloneRow} be a plain spread and still be a true deep copy, and it
 * mirrors the shape of a {@code cw.CwConsultationDocSectionValue} row, whose payload is generic
 * {@code gen_*} scalars.
 */
export type XxAutosaveFocusMockRow = { [attribute: string]: number | string | null };

/**
 * Fully IN-MEMORY CRUD service behind the autosave-focus POC page
 * ({@code xx/test/autosave-focus}) — it NEVER issues an HTTP request for the four methods it
 * overrides, so the POC runs with no backend at all.
 *
 * <h3>Why a mock and not a real entity</h3>
 * The POC has to prove a FRAMEWORK property: that an autosave round trip no longer destroys the
 * input the user is typing in, and that a dependent field can appear while focus stays in its
 * master. Both are pure frontend questions, and both need a configurable server LATENCY — the whole
 * second defect (keystrokes typed while the PUT is in flight being dropped by the {@code busy}
 * gate) is only observable when the response is slow enough to type across. A real endpoint gives
 * neither the latency dial nor a server-owned computed value that is guaranteed to change on every
 * save. {@link latencyMs} and {@code gen_serverStamp} are exactly those two instruments.
 *
 * <h3>The shape is a 1:1 mirror of a cw section-value form</h3>
 * {@link buildMockForm} delivers seven fields whose ids, controls and access mirror what cw renders
 * per consultation-document section line: technical {@code id} / {@code version} plus generic
 * {@code gen_*} attributes. {@code gen_hasDetails} is the MASTER of the dependent pair and
 * {@code gen_details} the DEPENDENT — note that the form itself always delivers the dependent as
 * editable ({@code access: 'update'}); hiding it is the PAGE's job through a
 * {@code MvsFormControlOverwrite}, exactly the way cw derives its hidden-field list client side.
 * A mock that shipped the dependency baked into {@code access} would not reproduce Path B at all.
 *
 * <h3>What is deliberately NOT overridden</h3>
 * Everything else — {@code list}, {@code listQl}, {@code history}, {@code getLight}, {@code restore},
 * {@code hardDelete} … — falls through to {@link MvsCrudService} and therefore WOULD issue a real
 * HTTP call against {@code /xx/autosaveFocusMock}, an endpoint that does not exist. That is
 * acceptable because the POC page mounts a single {@code mvs-crud-object-generic} with a fixed
 * {@code objectId}, which only ever calls {@code get} / {@code update} / {@code create} /
 * {@code delete} / {@code template}. Anything that lands on one of the inherited methods is a sign
 * the page grew a surface the mock was never meant to serve, and the resulting 404 says so loudly
 * instead of hiding behind a silently empty result.
 *
 * @ticket TM-1000002003
 */
@Injectable({
    providedIn: 'root'
})
export class XxAutosaveFocusMockService extends MvsCrudService {

    /**
     * Simulated server latency in milliseconds, applied to EVERY overridden method.
     *
     * A public static on purpose: the POC page writes it directly from its latency selector
     * ({@code XxAutosaveFocusMockService.latencyMs = 2500}) without needing a handle on the injected
     * instance, and every call reads it AT CALL TIME rather than capturing it once at construction.
     * Changing it therefore takes effect on the very next save, mid-session, which is what makes the
     * "type across a 2500 ms flight" acceptance step doable by hand.
     *
     * 0 is a legitimate value and still defers the emission by one macrotask ({@code delay(0)}), so
     * the response never arrives synchronously — a synchronous CRUD response is a shape no real
     * consumer ever sees and would hide re-entrancy defects.
     */
    public static latencyMs: number = 800;

    /**
     * Number of updates the mock has APPLIED, i.e. counted when the response is produced after the
     * latency has elapsed — not when the call was made. An update that is still in flight is not in
     * this number yet. Diagnostics only.
     */
    public updateCount: number = 0;

    /**
     * The attribute bag of the most recently APPLIED update, captured after the merge rules below
     * were applied (so server-owned attributes are already excluded). Counted at the same moment as
     * {@link updateCount}. Diagnostics only.
     */
    public lastUpdatePayload: XxAutosaveFocusMockRow | null = null;

    /**
     * The in-memory table. Pre-seeded with row 1, which is the row the POC page mounts.
     *
     * {@code gen_hasDetails} starts empty so the dependent {@code gen_details} starts HIDDEN — the
     * initial state acceptance step 3 types out of.
     */
    private readonly store: Map<number, XxAutosaveFocusMockRow> = new Map<number, XxAutosaveFocusMockRow>([
        [1, {
            id: 1,
            version: 0,
            gen_remark: 'Erster Eintrag',
            gen_amount: 100,
            gen_hasDetails: null,
            gen_details: null,
            gen_xyz: '',
            gen_serverStamp: null
        }]
    ]);

    /**
     * Attributes the CLIENT may never write. They are carried in the form (and therefore submitted
     * with every save — {@code version} is {@code hidden} and {@code id} / {@code gen_serverStamp}
     * are {@code read}, and all three still get a form control), but the value that arrives is the
     * snapshot the form loaded, which is by definition older than the stored one. Merging it back
     * would let a save silently revert the very column whose change the POC is trying to make
     * visible.
     */
    private static readonly SERVER_OWNED_ATTRIBUTES: string[] = ['id', 'version', 'gen_serverStamp'];

    /**
     * Keys of a {@link DtoDetail} that are framework plumbing rather than entity data and must not
     * reach a stored row. {@code _form} is the backing field of the lazily-created {@code form}
     * accessor, so reading {@code dto.form} would even MATERIALISE an empty form — it is filtered by
     * name here and never read.
     */
    private static readonly NON_ATTRIBUTE_KEYS: string[] = ['form', '_form', 'importObjectContext', 'subDtos'];

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/xx/autosaveFocusMock');
    }

    /**
     * Read one row, WITH its form — the call {@code mvs-crud-object-generic.refreshData()} makes on
     * mount ({@code get(objectId, ctx, true, true, 0, on_init_change, formMode, sgUseCaseId)}).
     *
     * The full parameter list of {@link MvsCrudService.get} is repeated so the override stays
     * signature compatible; every parameter but {@code id} is ignored, which is stated rather than
     * silently true. In particular {@code includeForm} is NOT honoured — the form is always
     * attached, because the only caller always asks for it and a formless branch would be untested
     * dead weight.
     *
     * {@code MvsCrudService.transformDtoOutToIn} is run exactly as {@code getInternal} runs it, so
     * the returned form goes through the same {@code MvsFormDto.create} wrapper the real path
     * produces. It is a no-op on the values themselves here (the row holds no dates), but keeping it
     * means the object graph the form component receives has the production shape.
     */
    public override get(id: number,
                        importObjectContext?: DtoImportObjectContext,
                        includeForm?: boolean,
                        resolveEntities?: boolean,
                        resolveEntityLevels?: number,
                        formAction?: FormActionEnum,
                        formMode?: FormModeEnum,
                        sgUseCaseId?: number): Observable<DtoDetail> {

        return of(null).pipe(
            delay(XxAutosaveFocusMockService.latencyMs),
            map(() => {

                const row: XxAutosaveFocusMockRow = this.requireRow(id);

                const dto: DtoDetail = this.toDto(row);
                dto.form = this.buildMockForm();

                MvsCrudService.transformDtoOutToIn(dto);

                // same notification the real getInternal() raises
                this.informObjectService(id, MvsObjectActionEnum.Accessed);

                return dto;
            })
        );
    }

    /**
     * Form metadata for a create — the call {@code mvs-crud-object-form} makes when it has no id.
     * The POC page never enters create mode, but the method is overridden anyway so that nothing on
     * this service can reach the network by accident.
     */
    public override template(obj: DtoTemplate): Observable<MvsFormDto> {

        // @ticket TM-1000002003: this is the SERVER-side half of Path C. The field SET is identical
        // every time — gen_xyz is always there — and only its `access` depends on what the form sent.
        // That stability is what lets the client apply the answer in place instead of rebuilding.
        const remark: any = obj && obj.dto ? obj.dto['gen_remark'] : null;
        const visible: boolean = String(remark ?? '').trim().length > 0;

        return of(null).pipe(
            delay(XxAutosaveFocusMockService.latencyMs),
            map(() => {
                const form: MvsFormDto = this.buildMockForm();
                form.formFields['gen_xyz'].access = visible ? 'update' : 'hidden';
                return form;
            })
        );
    }

    /**
     * Write one row and hand back what the "server" now holds.
     *
     * <h3>Everything happens AFTER the latency, never before</h3>
     * The merge, the version bump and the stamp are all inside the {@code map} that runs once the
     * delay has elapsed. That is not cosmetic: {@code gen_serverStamp} is the POC's proof that a
     * server-computed value landed in the form, so it has to carry the moment the response was
     * produced. Applying it at call time would print a timestamp from before the flight and make the
     * latency invisible in the one field built to show it.
     *
     * <h3>The caller's DTO is never mutated</h3>
     * {@link MvsCrudService.updateInternal} mutates what it is handed — {@code transformDtoInToOut}
     * rewrites its fields in place and {@code cleanDto} nulls its {@code form}. The mock deliberately
     * does not copy that: a POC that mutated the form component's outgoing snapshot would make the
     * "was this field touched during the flight?" comparison the in-place merge performs unreadable.
     *
     * The response carries NO form, mirroring the real update, which returns the persisted entity
     * only.
     */
    public override update(dto: DtoDetail, importObjectContext?: DtoImportObjectContext): Observable<DtoDetail> {

        return of(null).pipe(
            delay(XxAutosaveFocusMockService.latencyMs),
            map(() => {

                const id: number = Number(dto['id']);
                const row: XxAutosaveFocusMockRow = this.requireRow(id);

                const payload: XxAutosaveFocusMockRow = this.extractClientAttributes(dto);

                Object.assign(row, payload);
                row['version'] = Number(row['version'] ?? 0) + 1;
                row['gen_serverStamp'] = XxAutosaveFocusMockService.formatStamp(new Date());

                this.updateCount++;
                this.lastUpdatePayload = payload;

                const response: DtoDetail = this.toDto(row);

                // same two notifications the real updateInternal() raises
                this.informObjectService(id, MvsObjectActionEnum.Updated);
                this.subscribeBroadcastChange(response, ObjectChangeInformationActionEnum.updated);

                return response;
            })
        );
    }

    /**
     * Insert a new row under the next free id, stamped exactly like an update. Present for
     * completeness — the POC page mounts an existing row and never reaches this path.
     */
    public override create(dto: DtoDetail, importObjectContext?: DtoImportObjectContext): Observable<DtoDetail> {

        return of(null).pipe(
            delay(XxAutosaveFocusMockService.latencyMs),
            map(() => {

                const id: number = this.nextId();

                const row: XxAutosaveFocusMockRow = this.extractClientAttributes(dto);
                row['id'] = id;
                row['version'] = 0;
                row['gen_serverStamp'] = XxAutosaveFocusMockService.formatStamp(new Date());

                this.store.set(id, row);

                const response: DtoDetail = this.toDto(row);

                // same two notifications the real createInternal() raises
                this.informObjectService(id, MvsObjectActionEnum.Created);
                this.subscribeBroadcastChange(response, ObjectChangeInformationActionEnum.created);

                return response;
            })
        );
    }

    /**
     * Drop a row. Reported as {@code HARD} and {@code restorable: false} because that is what the
     * mock actually does — the row is gone from the map and there is no undo state behind it. A
     * {@code SOFT} answer would light up the generic component's undo affordance and then fail.
     */
    public override delete(id: number): Observable<DeleteResponseDto> {

        return of(null).pipe(
            delay(XxAutosaveFocusMockService.latencyMs),
            map(() => {

                const existed: boolean = this.store.delete(id);

                // same two notifications the real delete() raises
                this.informObjectService(id, MvsObjectActionEnum.Deleted);

                const deletedDto: DtoDetail = new DtoDetail();
                deletedDto.id = id;
                this.subscribeBroadcastChange(deletedDto, ObjectChangeInformationActionEnum.deleted);

                const response: DeleteResponseDto = {
                    success: existed,
                    deletionType: DeletionType.HARD,
                    restorable: false,
                    message: existed ? undefined : `Kein Eintrag mit der ID ${id} vorhanden.`
                };

                return response;
            })
        );
    }

    /**
     * The 1:1 form mock — seven fields mirroring a cw consultation-document section-value form.
     *
     * <h3>A FRESH instance on every call, never a shared one</h3>
     * {@link MvsCrudService.template} carries an explicit "MUST NOT BE CACHED" contract, because
     * consumers mutate the returned {@code MvsFormFieldDto} objects IN PLACE:
     * {@code MvsFormControlService} stamps {@code access} onto them when an overwrite hides a field,
     * filters {@code valueList.entries}, and applies label overrides directly. Handing out one
     * shared graph would let the HIDDEN stamp the POC page puts on {@code gen_details} outlive the
     * form that asked for it. Every call therefore builds its own object graph.
     *
     * <h3>Why {@code access: 'update'} and not {@code 'write'}</h3>
     * {@code 'update'} is the string the platform actually produces: the backend enum members are
     * {@code read | update | hidden} ({@code AccessConstants.FIELD_ACCESS}, serialised by name) and
     * {@code MvsFormControlOverwriteField.getAccessAsString()} returns the same three. Only
     * {@code 'hidden'} and {@code 'read'} are ever compared against in the render path, so any other
     * token would behave identically — which is exactly why picking a token the platform never emits
     * would be a silent fidelity leak in a fixture whose whole job is to be indistinguishable from a
     * real form.
     *
     * <h3>No {@code uiFieldGroup}, no {@code formFieldGroups}</h3>
     * {@code MvsFormControlService.buildFormGroup} pushes a DEFAULT field group holding every field
     * that declares no {@code uiFieldGroup} ({@code MvsFormGroup.getFieldsWithoutFieldGroup}). All
     * seven fields therefore render through the default group with no extra configuration.
     *
     * <h3>Why the master is a text field and not a select</h3>
     * Acceptance step 3 of the POC is "with focus in ‚Details vorhanden?' TYPE ja → ‚Details'
     * appears while focus stays in the master". A dropdown has no caret to keep and cannot be typed
     * into, so a select master would make the very property under test unobservable. The master is a
     * plain {@code text} control and the page compares its trimmed, lower-cased value against
     * {@code 'ja'}.
     */
    public buildMockForm(): MvsFormDto {

        return MvsFormDto.create({
            objectType: 'xx.XxAutosaveFocusMock',
            formComplete: true,
            formFields: {
                // technical columns, both carried by a control so they are submitted unchanged
                id: {
                    id: 'id', uiLabel: 'ID',
                    dataType: 'java.lang.Long', uiInputControl: 'text',
                    order: 1, access: 'read'
                },
                version: {
                    id: 'version', uiLabel: 'Version',
                    dataType: 'java.lang.Long', uiInputControl: 'text',
                    order: 2, access: 'hidden'
                },

                // generic payload — the cw gen_* shape
                // @ticket TM-1000002003: the Path C trigger — a change here makes the form ask the
                // server what it implies, and the answer decides whether gen_xyz is visible.
                gen_remark: {
                    id: 'gen_remark', uiLabel: 'Bemerkung', refreshFormOnChange: true,
                    dataType: 'java.lang.String', uiInputControl: 'textarea',
                    order: 10, access: 'update'
                },
                gen_amount: {
                    id: 'gen_amount', uiLabel: 'Betrag',
                    dataType: 'java.lang.Integer', uiInputControl: 'numberInt',
                    order: 11, access: 'update'
                },

                // MASTER of the dependent pair
                gen_hasDetails: {
                    id: 'gen_hasDetails', uiLabel: 'Details vorhanden?',
                    dataType: 'java.lang.String', uiInputControl: 'text',
                    order: 12, access: 'update'
                },
                // DEPENDENT — delivered EDITABLE on purpose; the page hides it via
                // MvsFormControlOverwrite while gen_hasDetails != 'ja'
                gen_details: {
                    id: 'gen_details', uiLabel: 'Details',
                    dataType: 'java.lang.String', uiInputControl: 'text',
                    order: 13, access: 'update', uiMandatory: true
                },

                // server-computed, so a save visibly lands a value the user never typed
                // @ticket TM-1000002003: Path C's dependent field. ALWAYS present, so the control
                // structure never changes and the in-place path stays applicable; only `access` moves.
                gen_xyz: {
                    id: 'gen_xyz', uiLabel: 'Nur bei Bemerkung',
                    dataType: 'java.lang.String', uiInputControl: 'text',
                    order: 14, access: 'hidden'
                },
                gen_serverStamp: {
                    id: 'gen_serverStamp', uiLabel: 'Server-Zeitstempel',
                    dataType: 'java.lang.String', uiInputControl: 'text',
                    order: 20, access: 'read'
                }
            }
        });
    }

    /** The row behind an id, or a loud failure — a silent empty row would look like a saved one. */
    private requireRow(id: number): XxAutosaveFocusMockRow {

        const row: XxAutosaveFocusMockRow = this.store.get(id);

        if (!row) {
            throw new Error(`XxAutosaveFocusMockService: kein Eintrag mit der ID ${id} vorhanden.`);
        }

        return row;
    }

    /** Next free id — max of the stored ids plus one, so a delete never frees an id for reuse. */
    private nextId(): number {
        return this.store.size === 0 ? 1 : Math.max(...Array.from(this.store.keys())) + 1;
    }

    /**
     * The attributes of an incoming DTO the client is actually allowed to write: everything that is
     * neither framework plumbing ({@link NON_ATTRIBUTE_KEYS}) nor server owned
     * ({@link SERVER_OWNED_ATTRIBUTES}), and that carries a defined value.
     *
     * {@code undefined} is dropped rather than stored, so an attribute the form did not send keeps
     * its stored value; an explicit {@code null} IS stored, because clearing a field is a legitimate
     * edit the form expresses exactly that way.
     */
    private extractClientAttributes(dto: DtoDetail): XxAutosaveFocusMockRow {

        const attributes: XxAutosaveFocusMockRow = {};

        for (const key of Object.keys(dto)) {

            if (XxAutosaveFocusMockService.NON_ATTRIBUTE_KEYS.indexOf(key) >= 0) {
                continue;
            }

            if (XxAutosaveFocusMockService.SERVER_OWNED_ATTRIBUTES.indexOf(key) >= 0) {
                continue;
            }

            const value: any = dto[key];

            if (value === undefined || typeof value === 'function') {
                continue;
            }

            attributes[key] = value;
        }

        return attributes;
    }

    /**
     * A stored row as a fresh {@link DtoDetail}. The copy is what makes the mock behave like a
     * server: the caller can hold on to it, the form can mutate it, and the stored row is unaffected
     * — the defect the POC reproduces is about STALE snapshots, so handing out a live reference
     * would quietly make the whole thing untestable.
     */
    private toDto(row: XxAutosaveFocusMockRow): DtoDetail {

        const dto: DtoDetail = new DtoDetail();

        for (const key of Object.keys(row)) {
            dto[key] = row[key];
        }

        return dto;
    }

    /** A row copy. A plain spread IS a deep copy here — see {@link XxAutosaveFocusMockRow}. */
    private cloneRow(row: XxAutosaveFocusMockRow): XxAutosaveFocusMockRow {
        return {...row};
    }

    /**
     * {@code HH:mm:ss.SSS} of the given moment — short enough to read at a glance in a form field
     * and precise enough to tell two saves inside one second apart, which a 0 ms latency run
     * produces routinely.
     */
    public static formatStamp(moment: Date): string {

        const pad = (value: number, length: number): string => String(value).padStart(length, '0');

        return `${pad(moment.getHours(), 2)}:${pad(moment.getMinutes(), 2)}:`
            + `${pad(moment.getSeconds(), 2)}.${pad(moment.getMilliseconds(), 3)}`;
    }

    /**
     * The stored row behind an id, as a COPY — the POC page's diagnostics panel reads it to show
     * what the "server" holds next to what the form holds. Returns null for an unknown id rather
     * than throwing, because a diagnostics read must never be able to break the page.
     */
    public peekRow(id: number): XxAutosaveFocusMockRow | null {

        const row: XxAutosaveFocusMockRow = this.store.get(id);

        return row ? this.cloneRow(row) : null;
    }

    /**
     * Put the mock back into its mounted state — row 1 as seeded, counters cleared. Lets the POC
     * page offer a "von vorn" control without a browser reload, which matters because a reload also
     * discards the event log the operator is reading.
     */
    public resetStore(): void {

        this.store.clear();
        this.store.set(1, {
            id: 1,
            version: 0,
            gen_remark: 'Erster Eintrag',
            gen_amount: 100,
            gen_hasDetails: null,
            gen_details: null,
            gen_xyz: '',
            gen_serverStamp: null
        });

        this.updateCount = 0;
        this.lastUpdatePayload = null;
    }

    override getObjectIcon(): string {
        return 'fa-regular fa-keyboard';
    }

    override getObjectLabels(): string[] {
        return ['Autosave-Fokus Testeintrag', 'Autosave-Fokus Testeinträge'];
    }

}
